import { useState, useEffect, useRef, useCallback } from "react";

import { getReport, streamReportChat } from "../../../api/reports";
import { scrapePublicCitation } from "../../../api/chat";

import { useRightSidebar } from "../../contexts/RightSidebarContext";
import { useCompanyContext } from "../../../context";
import ArticleDetailPanel from "../../Universal/ArticleCardDetailPanel";
import CitationLoadingAnimation from "../../Universal/CitationLoadingAnimation";
import { getTheme } from "../../Universal/useNavTheme";

import ReportDocument from "./ReportDocument";
import DefaultReportView from "./DefaultReportView";
import DefaultReportViewII from "./DefaultReportViewII";
import { getUserDatetime } from "./utils/reportUtils";

const isII = process.env.REACT_APP_IS_II;

/* ═══════════════════════════════════════════════════════════════════════════
   ReportInput — centered prompt input for new reports
   ═══════════════════════════════════════════════════════════════════════════ */

const ReportInput = ({ onSubmit }) => {
  const [value, setValue] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) onSubmit(value.trim());
    }
  };

  return (
    <div className="h-full flex flex-col items-center justify-center px-6 pt-14">
      <div className="w-full max-w-[600px]">
        <h2 className="text-lg font-medium text-zinc-800 dark:text-zinc-200 text-center mb-6">
          Describe the report you want in detail
        </h2>
        <div className="rounded-xl bg-white dark:bg-zinc-900 ring-1 ring-zinc-200 dark:ring-zinc-800 shadow-sm">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. Write a competitive analysis of the top 5 CRM platforms, focusing on pricing, features, and market share..."
            rows={5}
            className="w-full resize-none bg-transparent text-[14px] leading-relaxed px-4 pt-4 pb-2 text-zinc-900 placeholder:text-zinc-400 dark:text-zinc-100 dark:placeholder:text-zinc-600 focus:outline-none"
          />
          <div className="flex justify-end px-3 pb-3">
            <button
              onClick={() => value.trim() && onSubmit(value.trim())}
              disabled={!value.trim()}
              className={[
                "px-4 py-2 rounded-lg text-[13px] font-medium transition-colors",
                value.trim()
                  ? "bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200"
                  : "bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-600 cursor-not-allowed",
              ].join(" ")}
            >
              Generate Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MainReports — orchestrator
   ═══════════════════════════════════════════════════════════════════════════ */

const MainReports = () => {
  const [reportId, setReportId] = useState("default");
  const [reportTitle, setReportTitle] = useState("Untitled Report");
  const [reportContent, setReportContent] = useState("");
  const [reportCitations, setReportCitations] = useState([]);
  const [reportOptions, setReportOptions] = useState({});

  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingReportContent, setStreamingReportContent] = useState("");
  const [statusText, setStatusText] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const abortRef = useRef(null);
  const controlBufRef = useRef("");
  const fullReplaceAnimRef = useRef(null);

  const {
    isRightOpen,
    setIsRightOpen,
    setRightComponent,
    setRightSidebarMode,
  } = useRightSidebar();
  const { currentCompany } = useCompanyContext();
  const [activeCitationUrl, setActiveCitationUrl] = useState(null);
  const [citationInteractions, setCitationInteractions] = useState({});

  const handleCitationClick = useCallback(
    (citation) => {
      if (!citation.url) return;
      const citationKey = citation.url;
      if (activeCitationUrl === citationKey && isRightOpen) return;
      setActiveCitationUrl(citationKey);
      setRightSidebarMode(null);
      setRightComponent(
        <div className="h-full">
          <CitationLoadingAnimation />
        </div>,
      );
      setIsRightOpen(true);

      scrapePublicCitation({ url: citation.url })
        .then((res) => {
          const data = res?.data;
          if (!data?.success && !data?.already_saved) {
            setRightComponent(
              <div className="p-6 text-center text-[13px] text-zinc-500 dark:text-zinc-400">
                Unable to load source
              </div>,
            );
            return;
          }
          const dark = document.documentElement.classList.contains("dark");
          const t = getTheme(dark);
          if (data.like || data.dislike || data.catalog) {
            setCitationInteractions((prev) => ({
              ...prev,
              [citationKey]: {
                like: !!data.like,
                dislike: !!data.dislike,
                catalog: !!data.catalog,
              },
            }));
          }
          const article = {
            document_id: data.doc_id || citationKey,
            title: data.title || data.domain || citation.title || "Untitled",
            url: data.url || citation.url,
            source: data.source || data.domain || "",
            content: data.content || "",
            description: data.description || "",
            screenshot: data.screenshot || "",
            screenshot_full: data.screenshot_full || "",
            target_type: data.target_type || "web_citation",
            timestamp: data.date_published
              ? new Date(data.date_published).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              : "",
            like: !!data.like,
            dislike: !!data.dislike,
            catalog: !!data.catalog,
          };
          const onInteract = (_cardId, _docId, state) => {
            setCitationInteractions((prev) => ({
              ...prev,
              [citationKey]: state,
            }));
          };
          setRightComponent(
            <CitationLoadingAnimation
              isComplete={true}
              statusMessage="Source captured"
              onCompleteFinished={() => {
                setRightComponent(
                  <ArticleDetailPanel
                    article={article}
                    cardId={article.document_id}
                    t={t}
                    onInteract={onInteract}
                  />,
                );
              }}
            />,
          );
        })
        .catch(() => {
          setRightComponent(
            <div className="p-6 text-center text-[13px] text-zinc-500 dark:text-zinc-400">
              Unable to load source
            </div>,
          );
        });
    },
    [
      activeCitationUrl,
      isRightOpen,
      setIsRightOpen,
      setRightComponent,
      setRightSidebarMode,
    ],
  );

  useEffect(() => {
    if (!isRightOpen) setActiveCitationUrl(null);
  }, [isRightOpen]);

  // ── Sidebar events ──────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      const id = e.detail?.reportId;
      if (id) {
        setReportId(id);
        setIsRightOpen(false);
        setActiveCitationUrl(null);
      }
    };
    window.addEventListener("sidebar:report-selected", handler);
    return () => window.removeEventListener("sidebar:report-selected", handler);
  }, [setIsRightOpen]);

  useEffect(() => {
    const handler = (e) => {
      const id = e.detail?.reportId;
      if (id) {
        setReportId(id);
        setReportTitle("Untitled Report");
        setReportContent("");
        setReportCitations([]);
        setReportOptions({});
        setHasSubmitted(false);
        setIsRightOpen(false);
        setActiveCitationUrl(null);
        setCitationInteractions({});
      }
    };
    window.addEventListener("sidebar:new-report", handler);
    return () => window.removeEventListener("sidebar:new-report", handler);
  }, [setIsRightOpen]);

  useEffect(() => {
    if (reportId)
      window.dispatchEvent(
        new CustomEvent("sidebar:select-report", { detail: { reportId } }),
      );
  }, [reportId]);

  // ── Load report (skip for "default") ───────────────────────────────
  useEffect(() => {
    if (!reportId || reportId === "default") return;
    let cancelled = false;
    getReport(reportId)
      .then((res) => {
        const doc = res?.data || {};
        if (cancelled || doc.error) return;
        setReportTitle(doc.title || "Untitled Report");
        setReportContent(doc.report_content || "");
        setReportCitations(doc.report_citations || []);
        setReportOptions(doc.report_options || {});
        const citations = doc.report_citations || [];
        if (citations.length > 0) {
          const interactions = {};
          let changed = false;
          Promise.allSettled(
            citations
              .filter((c) => c.url)
              .map((c) =>
                scrapePublicCitation({ url: c.url }).then((r) => {
                  if (cancelled) return;
                  const d = r?.data;
                  if (d && (d.like || d.dislike || d.catalog)) {
                    interactions[c.url] = {
                      like: !!d.like,
                      dislike: !!d.dislike,
                      catalog: !!d.catalog,
                    };
                    changed = true;
                  }
                }),
              ),
          ).then(() => {
            if (!cancelled && changed)
              setCitationInteractions((prev) => ({ ...prev, ...interactions }));
          });
        }
      })
      .catch((err) => {
        if (!cancelled) console.error("Failed to load report:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  // ── Stream parsing ──────────────────────────────────────────────────
  const isControlStart = (s) =>
    s.startsWith('{"report_action"') ||
    s.startsWith('{"status"') ||
    s.startsWith('{"error"') ||
    s.startsWith('{"research_step"');

  const tryConsumeControl = useCallback((maybeJson) => {
    const trimmed = maybeJson.trim();
    if (!isControlStart(trimmed)) return { consumed: false };
    try {
      const obj = JSON.parse(trimmed);
      if (obj.error) {
        console.error("[reports/chat] Stream error:", obj.error);
        return { consumed: true };
      }
      if (obj.status) {
        setStatusText(obj.status);
        return { consumed: true };
      }
      if (obj.research_step) {
        const step = obj.research_step;
        const text = (step.text || "").trim();
        if (text) setStatusText((step.status === "done" ? "✓ " : "") + text);
        return { consumed: true };
      }
      switch (obj.report_action) {
        case "stream_start":
          if (obj.title) setReportTitle(obj.title);
          setStreamingReportContent("");
          break;
        case "stream_chunk":
          setStreamingReportContent((prev) => prev + (obj.content || ""));
          break;
        case "stream_done":
          setStreamingReportContent((prev) => {
            setReportContent(prev);
            const titleMatch = prev.match(/^#\s+(.+)$/m);
            if (titleMatch?.[1]?.trim()) setReportTitle(titleMatch[1].trim());
            return "";
          });
          break;
        case "full_replace":
          if (obj.title) setReportTitle(obj.title);
          if (obj.content !== undefined) {
            if (fullReplaceAnimRef.current) {
              clearInterval(fullReplaceAnimRef.current);
              fullReplaceAnimRef.current = null;
            }
            const fullContent = obj.content;
            const chunkSize = 150;
            let i = 0;
            setStreamingReportContent("");
            fullReplaceAnimRef.current = setInterval(() => {
              i += chunkSize;
              if (i >= fullContent.length) {
                clearInterval(fullReplaceAnimRef.current);
                fullReplaceAnimRef.current = null;
                setStreamingReportContent("");
                setReportContent(fullContent);
              } else {
                setStreamingReportContent(fullContent.slice(0, i));
              }
            }, 12);
          }
          break;
        case "citations":
          if (obj.citations) setReportCitations(obj.citations);
          break;
        case "set_option": {
          const { report_action: _, ...rest } = obj;
          setReportOptions((prev) => ({ ...prev, ...rest }));
          break;
        }
        case "saved":
          setIsStreaming(false);
          window.dispatchEvent(new CustomEvent("sidebar:refresh-reports"));
          break;
        default:
          break;
      }
      return { consumed: true };
    } catch {
      return { consumed: false, needsBuffer: true };
    }
  }, []);

  // ── Send message ────────────────────────────────────────────────────
  const handleSend = useCallback(
    async (message) => {
      if (!message.trim() || !reportId || isStreaming) return;
      const hasReport = reportContent && reportContent.trim().length > 0;
      const msg = hasReport
        ? message.trim()
        : message.trim() +
          "\n\nWrite the report now. Go ahead and generate the report.";
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      if (fullReplaceAnimRef.current) {
        clearInterval(fullReplaceAnimRef.current);
        fullReplaceAnimRef.current = null;
      }
      setIsStreaming(true);
      setHasSubmitted(true);
      setStreamingReportContent("");
      setStatusText("");
      controlBufRef.current = "";
      try {
        const response = await streamReportChat({
          reportId,
          message: msg,
          userDatetime: getUserDatetime(),
          signal: controller.signal,
        });
        if (!response.ok || !response.body)
          throw new Error(`HTTP error: ${response.status}`);
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let chatOutput = "";
        const processChunk = (raw) => {
          if (!raw.includes("{")) {
            chatOutput += raw;
            return;
          }
          const lines = raw.split("\n");
          let textAccum = "";
          for (let i = 0; i < lines.length; i++) {
            const t = lines[i].trim();
            if (t && isControlStart(t)) {
              if (textAccum) {
                chatOutput += textAccum;
                textAccum = "";
              }
              const res = tryConsumeControl(t);
              if (!res.consumed) controlBufRef.current = t;
            } else {
              if (textAccum.length > 0) textAccum += "\n";
              textAccum += lines[i];
            }
          }
          if (textAccum) chatOutput += textAccum;
        };
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          if (controlBufRef.current) {
            controlBufRef.current += chunk;
            if (controlBufRef.current.includes("\n")) {
              const buffered = controlBufRef.current;
              controlBufRef.current = "";
              processChunk(buffered);
            }
            continue;
          }
          processChunk(chunk);
        }
        const tail = decoder.decode();
        if (tail || controlBufRef.current) {
          const final = (controlBufRef.current || "") + (tail || "");
          controlBufRef.current = "";
          processChunk(final);
        }
      } catch (err) {
        if (err.name !== "AbortError") console.error("Report chat error:", err);
        setIsStreaming(false);
        setStreamingReportContent("");
        setStatusText("");
      }
    },
    [reportId, reportContent, isStreaming, tryConsumeControl],
  );

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (fullReplaceAnimRef.current) {
      clearInterval(fullReplaceAnimRef.current);
      fullReplaceAnimRef.current = null;
    }
    setIsStreaming(false);
    setStreamingReportContent("");
    setStatusText("");
  }, []);

  const handleExportPdf = useCallback(() => {
    const el = document.querySelector("[data-report-content]");
    if (!el) return;
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${reportTitle}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #1a1a1a; line-height: 1.7; font-size: 14px; }
  h1 { font-size: 22px; border-bottom: 1px solid #e5e5e5; padding-bottom: 8px; }
  h2 { font-size: 18px; margin-top: 24px; }
  h3 { font-size: 15px; margin-top: 20px; }
  hr { border: none; border-top: 1px solid #e5e5e5; margin: 16px 0; }
  table { border-collapse: collapse; width: 100%; margin: 12px 0; }
  th, td { border: 1px solid #d4d4d8; padding: 6px 10px; text-align: left; font-size: 13px; }
  th { background: #f4f4f5; font-weight: 600; }
  mark { background: #fef08a; padding: 1px 4px; border-radius: 2px; }
  code { background: #f4f4f5; padding: 2px 6px; border-radius: 4px; font-size: 13px; }
  pre { background: #f4f4f5; padding: 12px; border-radius: 8px; overflow-x: auto; }
  pre code { background: none; padding: 0; }
  a { color: #2563eb; }
</style></head><body>${el.innerHTML}</body></html>`;
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.document.title = `${reportTitle} - ${new Date().toLocaleDateString()}`;
      setTimeout(() => printWindow.print(), 250);
    }
  }, [reportTitle]);

  // ── Render ──────────────────────────────────────────────────────────

  // No report selected
  if (!reportId) {
    return (
      <div className="h-full flex items-center justify-center pt-14">
        <p className="text-[14px] text-zinc-400 dark:text-zinc-600">
          Select a report or create a new one to get started.
        </p>
      </div>
    );
  }

  // Default Report — grid of TileReport cards
  if (reportId === "default") {
    if (isII) return <DefaultReportViewII />;
    else return <DefaultReportView key={currentCompany?.case} />;
  }

  const hasReport = reportContent && reportContent.trim().length > 0;
  const showInput = !hasReport && !isStreaming && !hasSubmitted;

  if (showInput) return <ReportInput onSubmit={handleSend} />;

  if (!hasReport && !streamingReportContent) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-6 pt-14">
        <div className="flex items-center gap-3">
          <div className="w-4 h-4 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-[14px] text-zinc-500 dark:text-zinc-400">
            {statusText || "Generating report…"}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full relative">
      <ReportDocument
        title={reportTitle}
        content={reportContent}
        citations={reportCitations}
        streamingContent={streamingReportContent}
        isStreaming={isStreaming}
        isUndoing={false}
        undoTargetContent=""
        undoTargetTitle=""
        undoTargetCitations={[]}
        onUndoComplete={() => {}}
        reportOptions={reportOptions}
        onSourceClick={handleCitationClick}
        activeSourceId={activeCitationUrl}
        citationInteractions={citationInteractions}
      />

      {!isStreaming && hasReport && (
        <button
          onClick={handleExportPdf}
          className="absolute top-20 right-6 z-20 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-[12px] font-medium hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors shadow-md"
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          PDF
        </button>
      )}

      {isStreaming && statusText && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-zinc-900 shadow-md border border-zinc-200 dark:border-zinc-800">
          <div className="w-3 h-3 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-[12px] text-zinc-500 dark:text-zinc-400">
            {statusText}
          </span>
        </div>
      )}
    </div>
  );
};

export default MainReports;
